var m = require('mithril');
var prop = require('mithril/stream');
var key = require('../../libs/keybindings');
var modal = require('../../layout/components/modal');
var dataset = require('../../data/models/dataset');
var element = require('../../dashboards/models/element');

var CANONICAL_TYPES = {
  list: 'list',
  gauge: 'gauge',
  chart: 'chart',
  spacer: 'spacer',
  img: 'img',
  image: 'img',
  text: 'text',
  diagram: 'diagram',
  table: 'table',
  smarttable: 'smartTable',
  smartlist: 'smartList',
  smartchart: 'smartchart'
};

var DATASET_TYPES = [
  'list',
  'gauge',
  'chart',
  'spacer',
  'img',
  'graphic',
  'text'
];

var TYPE_OPTIONS = [
  { value: 'list', label: 'List' },
  { value: 'gauge', label: 'Gauge' },
  { value: 'chart', label: 'Chart' },
  { value: 'diagram', label: 'Diagram' },
  { value: 'spacer', label: 'Spacer' },
  { value: 'img', label: 'Image' },
  { value: 'text', label: 'Text' },
  { value: 'table', label: 'Table' },
  { value: 'smartTable', label: 'Smart Table' },
  { value: 'smartList', label: 'Smart List' },
  { value: 'smartchart', label: 'Smart Chart' }
];

function normalizeType(type) {
  if (!type) return type;
  if (typeof type !== 'string') {
    return type;
  }
  var keyName = type.replace(/\s+/g, '').toLowerCase();
  return CANONICAL_TYPES[keyName] || type;
}

function isDatasetType(type) {
  var normalized = normalizeType(type);
  return DATASET_TYPES.indexOf(normalized) !== -1;
}

function toGetter(value) {
  if (typeof value === 'function') {
    return value;
  }
  return function () { return value; };
}

function resolveContext(options) {
  if (!options) return null;

  if (options.location) {
    var getLocation = toGetter(options.location);
    return {
      datasetParentGetter: getLocation,
      elementParentGetter: function () {
        var location = getLocation();
        return location && location['@rid'];
      },
      elementExtras: {
        useHasElementEdge: true,
        locationGetter: options.location
      }
    };
  }

  if (options.equipment) {
    var getEquipment = toGetter(options.equipment);
    return {
      datasetParentGetter: getEquipment,
      elementParentGetter: function () {
        var equipment = getEquipment();
        return equipment && equipment['@rid'];
      },
      elementExtras: {
        useHasElementEdge: true,
        locationGetter: options.equipment
      }
    };
  }

  if (options.parent) {
    var getParent = toGetter(options.parent);
    var elementExtras = options.elementExtras || {};
    return {
      datasetParentGetter: getParent,
      elementParentGetter: function () {
        var parent = getParent();
        if (parent && typeof parent === 'object' && parent['@rid']) {
          return parent['@rid'];
        }
        return parent;
      },
      elementExtras: elementExtras
    };
  }

  return null;
}

function ensureDatasetDefaults(context) {
  if (!context) return;
  var parentGetter = context.datasetParentGetter;
  var parentRecord = parentGetter ? parentGetter() : null;
  dataset.form.parent(parentRecord || null);
  dataset.record = dataset.record || {};
  dataset.record.parent = parentRecord || null;
}

function ensureElementDefaults(context) {
  if (!context) return;
  var parentGetter = context.elementParentGetter;
  var parentRid = parentGetter ? parentGetter() : null;
  var extras = context.elementExtras || {};
  var useHasElementEdge = !!extras.useHasElementEdge;

  element.form.parent(parentRid || null);
  if (typeof element.form.useHasElementEdge === 'function') {
    element.form.useHasElementEdge(useHasElementEdge);
  }

  element.record = element.record || {};
  element.record.parent = parentRid || null;
  element.record.useHasElementEdge = useHasElementEdge;

  if (extras.locationGetter) {
    element.record.location = extras.locationGetter;
  } else if (element.record.location) {
    delete element.record.location;
  }
}

function focusModalRoot() {
  var modalRoot = document.getElementById('modal-id-to-focus');
  if (modalRoot) {
    modalRoot.focus();
  }
}

function setType(type, selectedType, usingDataset, context) {
  var normalized = normalizeType(type);
  selectedType(normalized);
  var datasetSelected = isDatasetType(normalized);
  usingDataset(datasetSelected);
  dataset.errors = [];
  element.errors = [];

  if (datasetSelected) {
    dataset.form.type(normalized);
    ensureDatasetDefaults(context);
  } else {
    element.form.type(normalized);
    ensureElementDefaults(context);
  }
}

function updateModalTitle(selectedType, typeSelectorExpanded) {
  if (typeSelectorExpanded()) {
    modal.vm.title('New Element');
    return;
  }

  modal.vm.title(element.modalTitle('New Element', {}, {
    type: selectedType(),
    showSelectedType: true,
    onClearType: function () {
      typeSelectorExpanded(true);
      modal.vm.title('New Element');
      m.redraw();
    }
  }));
}

function openModalBody(selectedType, usingDataset, context, typeSelectorExpanded) {
  return function () {
    dataset.hideTypeSelect = true;
    element.hideTypeSelect = true;
    var activeModel = usingDataset() ? dataset : element;
    var formOptions = {
      attrs: {
        hideNameInput: true,
        deferTypeSpecificFields: typeSelectorExpanded()
      }
    };

    return m('div', [
      typeSelectorExpanded() ? m('div.form-group.mb3', [
        m('div.db.mb1', { id: 'new-element-type-selector-label' }, 'Type'),
        m('div', {
          role: 'group',
          'aria-labelledby': 'new-element-type-selector-label'
        }, [
          element.renderTypeSelector({
            options: TYPE_OPTIONS,
            selectedType: selectedType,
            showEquipmentDataGroup: true,
            onselect: function (type) {
              setType(type, selectedType, usingDataset, context);
              typeSelectorExpanded(false);
              updateModalTitle(selectedType, typeSelectorExpanded);
            }
          })
        ])
      ]) : null,
      activeModel.renderNameField ? activeModel.renderNameField() : null,
      usingDataset() ? dataset.view(formOptions) : element.view(formOptions)
    ]);
  };
}

function open(options) {
  options = options || {};
  var context = resolveContext(options);
  if (!context) return;

  var datasetParentGetter = context.datasetParentGetter;
  var elementParentGetter = context.elementParentGetter;
  if (!datasetParentGetter || !elementParentGetter) return;

  var datasetParent = datasetParentGetter();
  var elementParentRid = elementParentGetter();

  if (!datasetParent || !elementParentRid) {
    return;
  }

  dataset.initForm({ parent: datasetParent });

  var elementInitRecord = { parent: elementParentRid };
  var extras = context.elementExtras || {};
  if (extras.useHasElementEdge) {
    elementInitRecord.useHasElementEdge = true;
  }
  if (extras.locationGetter) {
    elementInitRecord.location = extras.locationGetter;
  }
  if (extras.initialValues && typeof extras.initialValues === 'object') {
    Object.assign(elementInitRecord, extras.initialValues);
  }

  element.initForm(elementInitRecord);

  ensureDatasetDefaults(context);
  ensureElementDefaults(context);

  var selectedType = prop(normalizeType(options.defaultType || 'list'));
  var usingDataset = prop(true);
  var typeSelectorExpanded = prop(true);
  setType(selectedType(), selectedType, usingDataset, context);

  var submit = async function () {
    focusModalRoot();
    await new Promise(function (resolve) { return setTimeout(resolve, 0); });

    var useDataset = usingDataset();
    var model = useDataset ? dataset : element;

    if (useDataset) {
      ensureDatasetDefaults(context);
    } else {
      ensureElementDefaults(context);
    }

    return model.save()
      .then(function (result) {
        key.unbind('enter');
        if (options.submitCallback) {
          options.submitCallback(result);
        }
        modal.vm.close();
        m.redraw();
        return result;
      });
  };

  dataset.hideTypeSelect = true;
  element.hideTypeSelect = true;
  dataset.errors = [];
  element.errors = [];

  key.setScope('modal');
  key('enter', 'modal', submit);

  modal.once('closed', function () {
    dataset.hideTypeSelect = false;
    element.hideTypeSelect = false;
    key.unbind('enter');
    if (element.record && element.record.location) {
      delete element.record.location;
    }
  });

  modal.vm.title('New Element');
  modal.vm.body = openModalBody(selectedType, usingDataset, context, typeSelectorExpanded);
  var closeModal = function () {
    key.unbind('enter');
    modal.vm.close();
  };

  modal.vm.buttons([
    {
      label: 'Cancel',
      class: 'dib mr2',
      func: closeModal
    },
    {
      label: 'Submit',
      class: 'dib btn-good green',
      func: submit
    }
  ]);

  modal.vm.open();
}

module.exports = {
  open: open
};
